/**
 * App Startup Data Prefetcher
 * Loads slow-changing data into memory on app init so UI components
 * don't need to fetch them individually.
 * 
 * Cached data: brand categories, rack structure, master categories, OE discount, banners
 * TTL: 5 minutes (refreshes in background)
 */

const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

const cache = {
  brands: { data: null, ts: 0, loading: false },
  masterCategories: { data: null, ts: 0, loading: false },
  rackStructure: { data: null, ts: 0, loading: false },
  banners: { data: null, ts: 0, loading: false },
  oeDiscount: { data: null, ts: 0, loading: false },
};

const API = process.env.REACT_APP_BACKEND_URL;

function getToken() {
  try {
    return localStorage.getItem('token') || sessionStorage.getItem('token');
  } catch {
    return null;
  }
}

async function fetchWithAuth(url) {
  const token = getToken();
  const headers = {};
  if (token) headers.Authorization = `Bearer ${token}`;
  try {
    const res = await fetch(`${API}${url}`, {
      headers,
      signal: AbortSignal.timeout(8000),
    });
    if (!res.ok) return null;
    return await res.json();
  } catch {
    return null;
  }
}

async function _getOrFetch(key, fetchFn) {
  const entry = cache[key];
  if (entry.data && Date.now() - entry.ts < CACHE_TTL) return entry.data;
  if (entry.loading) return entry.data;
  entry.loading = true;
  try {
    const result = await fetchFn();
    if (result !== null && result !== undefined) {
      entry.data = result;
      entry.ts = Date.now();
    }
  } catch { /* fallback to stale */ }
  entry.loading = false;
  return entry.data;
}

/** Get active brand categories with images (cached) */
export async function getCachedBrands() {
  return _getOrFetch('brands', async () => {
    const data = await fetchWithAuth('/api/categories/with-images');
    return data?.categories || null;
  });
}

/** Get master categories (cached) */
export async function getCachedMasterCategories() {
  return _getOrFetch('masterCategories', async () => {
    const data = await fetchWithAuth('/api/master-categories/active');
    return (data?.success && data?.categories) ? data.categories : null;
  });
}

/** Get banners (cached) */
export async function getCachedBanners() {
  return _getOrFetch('banners', async () => {
    return await fetchWithAuth('/api/banners');
  });
}

/** Get OE discount for current user (cached) */
export async function getCachedOeDiscount() {
  return _getOrFetch('oeDiscount', async () => {
    const data = await fetchWithAuth('/api/customer/oe-discount');
    return data?.oe_discount_percentage ?? null;
  });
}

/** Get rack structure for Nangli Puna (cached) */
export async function getCachedRackStructure() {
  return _getOrFetch('rackStructure', async () => {
    return await fetchWithAuth('/api/warehouse/structure/nangli_puna');
  });
}

/** Prefetch all startup data (call once on app mount) */
export function prefetchStartupData() {
  // Fire all fetches in parallel, don't await — let them populate cache in background
  getCachedBrands().catch(() => {});
  getCachedMasterCategories().catch(() => {});
  getCachedBanners().catch(() => {});
  getCachedRackStructure().catch(() => {});
  // OE discount needs auth — only prefetch if logged in
  if (getToken()) {
    getCachedOeDiscount().catch(() => {});
  }
}

/** Invalidate a specific cache (call after admin edits brands/racks) */
export function invalidateCache(key) {
  if (cache[key]) {
    cache[key].data = null;
    cache[key].ts = 0;
  }
}

/** Invalidate all caches (call on logout) */
export function invalidateAllCaches() {
  Object.keys(cache).forEach(key => {
    cache[key].data = null;
    cache[key].ts = 0;
  });
}
